import { facturaDialogSx } from "../../facturasStyles";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  ConfirmationNumber as InvoiceIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import MDTypography from "shared/components/MDTypography";
import MDBox from "shared/components/MDBox";
import { updateInvoiceService } from "modules/financiera/services/facturaService";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  light: "#dbe5eb",
  white: "#ffffff",
  gray: "#f5f5f5",
  darkGray: "#333333",
  error: "#d32f2f",
  success: "#2e7d32",
  warning: "#ed6c02",
};

const ModalEditarFactura = ({ open, onClose, factura, onSuccess }) => {
  const [formData, setFormData] = useState({
    invoice_number: "",
    nit: "",
    company: "",
    amount: "",
    code: "", // NUEVO CAMPO
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (factura && open) {
      setFormData({
        invoice_number: factura.invoice_number || factura.invoiceNumber || "",
        nit: factura.nit || "",
        company: factura.company || "",
        amount: factura.amount || "",
        code: factura.codigoEmpresarial || factura.code || "", // NUEVO CAMPO
      });
      setError(null);
      setSuccess(false);
    }
  }, [factura, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.invoice_number.trim()) {
      setError("El número de factura es requerido");
      return false;
    }
    if (!formData.nit.trim()) {
      setError("El NIT es requerido");
      return false;
    }
    if (!formData.company.trim()) {
      setError("La empresa es requerida");
      return false;
    }
    if (!formData.amount || formData.amount <= 0) {
      setError("El monto debe ser mayor a 0");
      return false;
    }
    // El código empresarial es opcional, no lo validamos como requerido
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const facturaId = factura.id || factura.Id;
      const result = await updateInvoiceService(facturaId, {
        invoice_number: formData.invoice_number,
        nit: formData.nit,
        company: formData.company,
        amount: formData.amount,
        code: formData.code, // NUEVO CAMPO
      });

      if (result.success) {
        setSuccess(true);
        if (onSuccess) {
          onSuccess(result.data || result);
        }
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.error || "Error al actualizar la factura");
      }
    } catch (err) {
      setError(err.message || "Error al guardar los cambios");
    } finally {
      setLoading(false);
    }
  };

  const formatAmountDisplay = (value) => {
    if (!value) return "";
    return new Intl.NumberFormat("es-CO").format(value);
  };

  const getStatusInfo = () => {
    const status = factura?.status;
    if (status === "pendiente") {
      return { text: "Pendiente", color: colors.warning, icon: "⏳" };
    }
    if (status === "pagado") {
      return { text: "Pagado", color: colors.success, icon: "✅" };
    }
    return { text: status || "Desconocido", color: colors.darkGray, icon: "ℹ️" };
  };

  const statusInfo = getStatusInfo();

  return (
    <Dialog
      open={open}
      onClose={!loading && onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...facturaDialogSx,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          color: colors.white,
          py: 3,
          px: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              borderRadius: 2,
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EditIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.white }}>
              Editar Factura
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Modifique los datos de la factura
            </MDTypography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small" sx={{ color: colors.white }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: "#e8f5e9",
                color: colors.success,
                "& .MuiAlert-icon": {
                  color: colors.success,
                },
              }}
            >
              <MDTypography variant="body2" fontWeight="bold">
                ¡Factura actualizada exitosamente!
              </MDTypography>
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: "#ffebee",
                color: colors.error,
                "& .MuiAlert-icon": {
                  color: colors.error,
                },
              }}
              onClose={() => setError(null)}
            >
              <MDTypography variant="body2" fontWeight="bold">
                {error}
              </MDTypography>
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              bgcolor: colors.gray,
              borderRadius: 3,
              p: 3,
              mb: 4,
              border: `1px solid ${colors.light}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <InfoIcon sx={{ color: colors.secondary, fontSize: 20 }} />
              <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: colors.primary }}>
                Información de la Factura
              </MDTypography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <MDTypography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                  ID Factura
                </MDTypography>
                <MDTypography variant="body2" fontWeight="bold" sx={{ color: colors.darkGray }}>
                  #{factura?.id || factura?.Id || "N/A"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDTypography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                  Estado Actual
                </MDTypography>
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <span>{statusInfo.icon}</span>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: statusInfo.color }}>
                    {statusInfo.text}
                  </MDTypography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDTypography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                  Fecha Creación
                </MDTypography>
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <ScheduleIcon sx={{ fontSize: 14, color: colors.secondary }} />
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: colors.darkGray }}>
                    {factura?.createdAt
                      ? new Date(factura.createdAt).toLocaleString("es-CO")
                      : factura?.createdAtFormatted || "N/A"}
                  </MDTypography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Box mb={3}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <EditIcon sx={{ color: colors.secondary, fontSize: 20 }} />
              <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: colors.primary }}>
                Datos Editables
              </MDTypography>
            </Box>
            <Divider sx={{ mb: 3, borderColor: colors.light }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="invoice_number"
                  label="Número de Factura"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Ej: FAC-001"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InvoiceIcon sx={{ color: colors.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      "&:hover fieldset": {
                        borderColor: colors.secondary,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.secondary,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.secondary,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="code"
                  label="Código Empresarial"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Ej: EMP-001"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CodeIcon sx={{ color: colors.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      "&:hover fieldset": {
                        borderColor: colors.secondary,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.secondary,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.secondary,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nit"
                  label="NIT"
                  value={formData.nit}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Ej: 900.123.456-7"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptIcon sx={{ color: colors.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      "&:hover fieldset": {
                        borderColor: colors.secondary,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.secondary,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.secondary,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="company"
                  label="Empresa"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Ej: Gopass S.A.S"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: colors.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      "&:hover fieldset": {
                        borderColor: colors.secondary,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.secondary,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.secondary,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="amount"
                  label="Monto"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="0"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon sx={{ color: colors.secondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <MDTypography variant="caption" sx={{ color: "text.secondary" }}>
                          COP
                        </MDTypography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      "&:hover fieldset": {
                        borderColor: colors.secondary,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.secondary,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.secondary,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {factura && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 3,
                bgcolor: "#f0fdf4",
                borderRadius: 3,
                border: `1px solid ${colors.light}`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <InfoIcon sx={{ color: colors.secondary, fontSize: 20 }} />
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: colors.primary }}>
                  Resumen de Cambios
                </MDTypography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: colors.white,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary" }}
                    >
                      Valor Original
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: colors.darkGray }}>
                      ${formatAmountDisplay(factura.amount)}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      display="block"
                      mt={1}
                      sx={{ color: "text.secondary" }}
                    >
                      N° Factura: {factura.invoice_number || factura.invoiceNumber}
                    </MDTypography>
                    {factura.codigoEmpresarial && (
                      <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ color: "text.secondary" }}
                      >
                        Código: {factura.codigoEmpresarial}
                      </MDTypography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: colors.white,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary" }}
                    >
                      Nuevo Valor
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: colors.success }}>
                      ${formatAmountDisplay(formData.amount)}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      display="block"
                      mt={1}
                      sx={{ color: "text.secondary" }}
                    >
                      N° Factura: {formData.invoice_number || "—"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary" }}
                    >
                      Código: {formData.code || "—"}
                    </MDTypography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: colors.light, mx: 0 }} />
      <DialogActions
        sx={{
          p: 3,
          px: 4,
          gap: 2,
          backgroundColor: colors.white,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          startIcon={<CancelIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1,
            borderColor: colors.light,
            color: colors.darkGray,
            "&:hover": {
              borderColor: colors.error,
              color: colors.error,
              backgroundColor: "#ffebee",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{
            color: colors.white,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1,
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
            },
            "&:disabled": {
              backgroundColor: colors.light,
            },
          }}
        >
          {loading ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ModalEditarFactura.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  factura: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoice_number: PropTypes.string,
    invoiceNumber: PropTypes.string,
    nit: PropTypes.string,
    company: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    createdAt: PropTypes.string,
    createdAtFormatted: PropTypes.string,
    codigoEmpresarial: PropTypes.string,
    code: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

ModalEditarFactura.defaultProps = {
  factura: null,
  onSuccess: null,
};

export default ModalEditarFactura;
